<template>
  <div class="wizard">
    <div class="wizard__topbar">
      <button
        v-if="currentStep > 0"
        type="button"
        class="wizard__nav-button"
        @click="previousStep"
      >
        <SVGIcon
          class="wizard__nav-icon"
          :iconName="'chevron_left'"
          :width="'40px'"
          :height="'40px'"
        />
        <span class="wizard__nav-label">Back</span>
      </button>
      <div v-else class="wizard__nav-spacer" />

      <h3 class="wizard__tab-name">{{ steps[currentStep] }}</h3>

      <button
        v-if="currentStep < steps.length - 1"
        type="button"
        class="wizard__nav-button"
        @click="nextStep"
      >
        <span class="wizard__nav-label">Next</span>
        <SVGIcon
          class="wizard__nav-icon"
          :iconName="'chevron_right'"
          :width="'40px'"
          :height="'40px'"
        />
      </button>
      <div v-else class="wizard__nav-spacer" />
    </div>

    <div v-if="currentStep === 0" class="wizard__content">
      <div class="wizard__field">
        <SchemaProject
          v-model="form.project_id"
          :field="{ name: 'project', label: 'Project' }"
          :parent="'select-val-session'"
        />
      </div>

      <div class="wizard__field">
        <SchemaExperiment
          v-model="form.experiment_id"
          :projectId="form.project_id || 0"
          :field="{ name: 'experiment', label: 'Experiment' }"
          :parent="'select-val-session'"
        />
      </div>

      <div class="wizard__field">
        <label for="select-trainer-select-val-session">Trainer</label>
        <select
          id="select-trainer-select-val-session"
          v-model="form.trainer_id"
          class="select"
          :disabled="!form.experiment_id"
        >
          <option :value="null" disabled selected>Select Trainer</option>
          <option
            v-for="trainer in trainers"
            :key="trainer.id"
            :value="trainer.id"
          >
            {{ trainer.name || `Session ${trainer.id}` }}
          </option>
        </select>
      </div>

      <div class="wizard__field">
        <SchemaArtifacts
          v-model="form.artifact"
          :trainerId="form.trainer_id || 0"
          :field="{ name: 'artifact', label: 'Artifacts' }"
          :parent="'select-val-session'"
        />
      </div>
    </div>

    <div v-else-if="currentStep === 1" class="wizard__content">
      <div class="wizard__field">
        <SchemaDataset
          :schemaField="{ name: 'dataset', label: 'Dataset' }"
          :parent="'select-val-session'"
          :enableProjectSelection="true"
          :enableTagsSelection="true"
          :enableAnnotationSetSelection="true"
          :modelValue="datasetSelectorModel"
          @update:modelValue="handleDatasetModelChange"
          v-model:datasetId="form.dataset_id"
          v-model:tagName="form.tag_name"
          v-model:annotationSetId="form.annotation_set_id"
        />
      </div>
    </div>

    <div v-else-if="currentStep === 2" class="wizard__content">
      <div class="wizard__field wizard__field--validation">
        <h4 class="wizard__platforms-heading">Validation Schema</h4>
        <SchemaGroup
          v-if="validationFields"
          :key="validationSchemaKey"
          v-model:params="validationSchemaParams"
          :fields="validationFields"
        />
        <p v-else class="wizard__placeholder">{{ validationSchemaMessage }}</p>
      </div>
    </div>

    <div v-else-if="currentStep === 3" class="wizard__content wizard__content--platforms">
      <div class="wizard__platforms-layout">
        <div class="wizard__platforms-panel wizard__platforms-panel--left">
          <h4 class="wizard__platforms-heading">Available Platforms</h4>
          <VendorList />
        </div>

        <div
          class="wizard__platforms-panel wizard__platforms-panel--right"
          :class="{ 'wizard__platforms-panel--drop-hover': isDropHovering }"
          @dragover.prevent="handleDropOver"
          @dragleave="handleDropLeave"
          @drop.prevent="handlePlatformDrop"
        >
          <h4 class="wizard__platforms-heading">Selected Platforms</h4>
          <SelectedPlatformsList
            :items="selectedPlatformItems"
            :removable="true"
            @remove-item="handleRemoveSelectedPlatform"
          />
        </div>
      </div>
    </div>

    <div v-else class="wizard__content">
      <p class="wizard__placeholder">This step will be added next.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import SVGIcon from '@/components/SVGIcon.vue';
import SchemaProject from '@/components/Schema/SchemaProject.vue';
import SchemaExperiment from '@/components/Schema/SchemaExperiment.vue';
import SchemaArtifacts from '@/components/Schema/SchemaArtifacts.vue';
import SchemaDataset from '@/components/Schema/SchemaDataset.vue';
import SchemaGroup from '@/components/Schema/SchemaGroup.vue';
import VendorList from '@/components/Platforms/VendorList.vue';
import SelectedPlatformsList from '@/components/Platforms/SelectedPlatformsList.vue';

const props = defineProps({
  profileId: {
    type: Number,
    default: 0,
  },
  projectId: {
    type: Number,
    default: 0,
  },
  experimentId: {
    type: Number,
    default: 0,
  },
  trainSessionId: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['step-change', 'create']);

const steps = ['Model', 'Dataset', 'Validation', 'Platforms'];
const currentStep = ref(0);
const trainers = ref([]);
const selectedPlatformItems = ref([]);
const isDropHovering = ref(false);
const schemaList = ref([]);
const schema = ref(null);
const validationSchemaParams = ref({});
const validationSchemaKey = ref(0);
const dataConnect = new DatastoreConnect();

const form = reactive({
  profile_id: 0,
  project_id: null,
  experiment_id: null,
  trainer_id: null,
  artifact: '',
  dataset_id: null,
  tag_name: '',
  annotation_set_id: null,
  validation_type: '',
  validation_params: {},
  selected_platforms: [],
});

const datasetSelectorModel = computed(() => ({
  project_id: form.project_id,
  dataset_id: form.dataset_id,
  tag_name: form.tag_name,
  annotation_set_id: form.annotation_set_id,
}));

const selectedTrainerSession = computed(() => (
  trainers.value.find((session) => session.id === form.trainer_id) || null
));

const validationFields = computed(() => schema.value?.schema || null);

const validationSchemaMessage = computed(() => {
  if (!form.trainer_id) {
    return 'Select a trainer in Model tab to load validation schema.';
  }

  if (schemaList.value.length === 0) {
    return 'Validation schema is not available.';
  }

  const modelType = selectedTrainerSession.value?.model;
  if (modelType) {
    return `No schema available for model type: ${modelType}`;
  }

  return 'No schema available for selected trainer.';
});

watch(() => form.experiment_id, async (experimentId) => {
  if (!experimentId) {
    trainers.value = [];
    form.trainer_id = null;
    return;
  }

  try {
    const response = await dataConnect.getTrainingSessionList({
      trainer_id: parseInt(experimentId, 10),
      reverse: true,
    });

    if (response?.error) {
      throw response.error;
    }

    trainers.value = response?.result || [];
    if (!trainers.value.some((session) => session.id === form.trainer_id)) {
      form.trainer_id = trainers.value[0]?.id || null;
    }
  } catch (error) {
    console.error('Error fetching training sessions:', error);
    trainers.value = [];
    form.trainer_id = null;
  }
}, { immediate: true });

watch(currentStep, (value) => {
  emit('step-change', value);
});

watch([selectedTrainerSession, schemaList], () => {
  const modelType = selectedTrainerSession.value?.model;
  const nextSchema = schemaList.value.find((item) => item?.type === modelType) || null;
  const previousType = schema.value?.type || '';
  schema.value = nextSchema;

  if ((nextSchema?.type || '') !== previousType) {
    validationSchemaParams.value = {};
    validationSchemaKey.value += 1;
  }
}, { immediate: true });

watch(validationSchemaParams, (params) => {
  form.validation_params = { ...(params || {}) };
}, { deep: true, immediate: true });

watch(schema, (currentSchema) => {
  form.validation_type = currentSchema?.name || '';
}, { immediate: true });

function goToStep(stepIndex) {
  currentStep.value = stepIndex;
}

function handleDatasetModelChange(value) {
  if (!value || typeof value !== 'object') {
    return;
  }

  form.project_id = value.project_id ?? form.project_id;
  form.dataset_id = value.dataset_id ?? null;
  form.tag_name = value.tag_name ?? '';
  form.annotation_set_id = value.annotation_set_id ?? null;
}

function initializeForm() {
  form.profile_id = props.profileId || 0;
  form.project_id = props.projectId || null;
  form.experiment_id = props.experimentId || null;
  form.trainer_id = props.trainSessionId || null;
  form.artifact = '';
  form.dataset_id = null;
  form.tag_name = '';
  form.annotation_set_id = null;
  form.validation_type = '';
  form.validation_params = {};
  form.selected_platforms = [];
  validationSchemaParams.value = {};
  validationSchemaKey.value += 1;
  selectedPlatformItems.value = [];
  currentStep.value = 0;
  emit('step-change', currentStep.value);

  void getValidatorSchema();
}

async function getValidatorSchema() {
  try {
    const data = await dataConnect.getValidatorSchema();
    schemaList.value = Array.isArray(data?.result) ? data.result : [];
    validationSchemaKey.value += 1;
  } catch (error) {
    schemaList.value = [];
    validationSchemaKey.value += 1;
  }
}

function handleDropOver() {
  isDropHovering.value = true;
}

function handleDropLeave() {
  isDropHovering.value = false;
}

function handlePlatformDrop(event) {
  isDropHovering.value = false;
  const transfer = event?.dataTransfer;
  if (!transfer) {
    return;
  }

  const raw = transfer.getData('application/platform-item') || transfer.getData('text/plain');
  if (!raw) {
    return;
  }

  try {
    const parsed = JSON.parse(raw);
    const vendor = parsed?.vendor || {};
    const platform = parsed?.platform || {};
    const key = getPlatformKey(platform, vendor);
    if (!key) {
      return;
    }

    const exists = selectedPlatformItems.value.some((item) => getPlatformKey(item.platform, item.vendor) === key);
    if (exists) {
      return;
    }

    selectedPlatformItems.value = [
      ...selectedPlatformItems.value,
      { vendor, platform },
    ];
  } catch (error) {
    // Ignore malformed drag payloads.
  }
}

function getPlatformKey(platform, vendor) {
  return String(
    platform?.id
    || platform?.code
    || platform?.name
    || `${vendor?.id || vendor?.code || vendor?.name || ''}`,
  );
}

function handleRemoveSelectedPlatform(payload) {
  const index = Number(payload?.index);
  if (!Number.isInteger(index) || index < 0 || index >= selectedPlatformItems.value.length) {
    return;
  }

  selectedPlatformItems.value = selectedPlatformItems.value.filter((_, idx) => idx !== index);
}

function isLastStep() {
  return currentStep.value === steps.length - 1;
}

function nextStep() {
  if (!isLastStep()) {
    currentStep.value += 1;
  }
}

function previousStep() {
  if (currentStep.value <= 0) {
    return;
  }

  currentStep.value -= 1;
}

function createSession() {
  emit('create', { ...form });
}

defineExpose({
  initializeForm,
  isLastStep,
  nextStep,
  previousStep,
  createSession,
});

initializeForm();
</script>

<style lang="scss" scoped>
.wizard {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.wizard__topbar {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 0 0 auto;
  min-height: 40px;
}

.wizard__nav-spacer {
  width: 130px;
  height: 44px;
}

.wizard__nav-button {
  border: gray solid 1px;
  border-radius: 6px;
  background: transparent;
  color: #0b2f6b;
  padding: 8px 38px;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1;
  cursor: pointer;
  width: 130px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  &:hover {
    background: rgba(107, 162, 105, 0.855);
  }
}

.wizard__nav-label {
  font-size: 1.25rem;
  font-weight: 700;
}

.wizard__nav-icon {
  flex: 0 0 auto;
}

.wizard__nav-icon :deep(svg) {
  width: 56px;
  height: 56px;
}

.wizard__tab-name {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  text-align: center;
  font-size: 1.9rem;
  font-weight: 700;
  pointer-events: none;
}

.wizard__content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  gap: 10px;
  padding: 8px 0;
  overflow-y: auto;
}

.wizard__placeholder {
  margin: 0;
  color: var(--body-text-color-secondary);
  font-size: 0.85rem;
}

.wizard__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
}

.wizard__field--validation {
  max-width: 760px;
}

.wizard__platforms-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1 1 auto;
  min-height: 0;
  padding: 14px;
  overflow: hidden;
  border-radius: 12px;
  @include themify() {
    border: 1px solid themed('color-primary-200');
    background: themed('color-white-100');
  }
}

.wizard__platforms-panel :deep(.vendor-list) {
  flex: 1 1 auto;
  min-height: 0;
}

.wizard__content--platforms {
  overflow: hidden;
}

.wizard__platforms-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px;
  flex: 1 1 auto;
  min-height: 0;
}

.wizard__platforms-panel--left {
  width: 100%;
}

.wizard__platforms-panel--right {
  min-width: 0;
  overflow-y: auto;
}

.wizard__platforms-panel--drop-hover {
  @include themify() {
    border-color: themed('color-primary');
  }
}

.wizard__platforms-heading {
  margin: 0 0 12px;
  font-size: 1rem;
  font-weight: 700;
}

@media (max-width: 960px) {
  .wizard__platforms-panel {
    padding: 12px;
  }

  .wizard__platforms-layout {
    grid-template-columns: 1fr;
  }
}
</style>
